import { PageHeader, Card, Pill } from "@/components/common";
import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, Zap, Crown, Rocket } from "lucide-react";

const PLANS = [
  { key: "solo", name: "Solo Operator", price: 0, icon: Rocket, tone: "slate", lookup: null, features: ["1 store", "50 AI designs / mo", "Manual review queue", "Basic analytics"], cta: "Current plan", current: true },
  { key: "pro", name: "Pro Autopilot", price: 49, icon: Zap, tone: "emerald", lookup: "pro_monthly", features: ["5 stores", "500 AI designs / mo", "Auto-approve trusted categories", "Ad optimization signals", "Priority research agent"], cta: "Upgrade", highlight: true },
  { key: "agency", name: "Agency", price: 199, icon: Crown, tone: "blue", lookup: "agency_monthly", features: ["Unlimited stores", "Unlimited AI generations", "Multi-tenant / team seats", "White-label dashboard", "Dedicated support"], cta: "Upgrade" },
];

export default function Billing() {
  const [busy, setBusy] = useState(null);
  const [busySq, setBusySq] = useState(null);

  const upgrade = async (plan) => {
    if (!plan.lookup) return;
    setBusy(plan.key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: plan.lookup, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e, "Could not start checkout"));
      setBusy(null);
    }
  };

  const squareUpgrade = async (plan) => {
    if (!plan.lookup) return;
    setBusySq(plan.key);
    try {
      const { data } = await api.post("/square/checkout", { lookup_key: plan.lookup, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e, "Could not start Square checkout"));
      setBusySq(null);
    }
  };

  return (
    <>
      <PageHeader title="Billing & Plans" subtitle="Powered by Stripe & Square. Upgrade to unlock more stores, autonomy and AI generation volume." testid="billing-header">
        <Pill tone="emerald">Stripe + Square · Test</Pill>
      </PageHeader>

      <div className="flex items-center gap-2 text-xs text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded-lg px-4 py-2.5">
        Live checkout in TEST mode. Stripe test card <span className="font-mono text-blue-200">4242 4242 4242 4242</span> · Square sandbox card <span className="font-mono text-blue-200">4111 1111 1111 1111</span> (any future expiry &amp; CVC). Real charges begin only after you go live.
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {PLANS.map((p) => (
          <Card key={p.key} className={`p-6 relative ${p.highlight ? "border-emerald-500/40 shadow-[0_0_30px_rgba(16,185,129,0.12)]" : ""}`} data-testid={`plan-card-${p.key}`}>
            {p.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><Pill tone="emerald">Most Popular</Pill></div>}
            <div className="flex items-center gap-2">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${p.tone === "emerald" ? "bg-emerald-500/15 text-emerald-400" : p.tone === "blue" ? "bg-blue-500/15 text-blue-400" : "bg-slate-800 text-slate-400"}`}><p.icon className="w-5 h-5" /></div>
              <h3 className="font-head text-lg font-semibold text-slate-100">{p.name}</h3>
            </div>
            <div className="mt-4 flex items-end gap-1">
              <span className="font-head text-4xl font-extrabold text-slate-100">${p.price}</span>
              <span className="text-slate-500 mb-1 text-sm">/mo</span>
            </div>
            <ul className="mt-5 space-y-2">
              {p.features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-slate-300"><Check className="w-4 h-4 text-emerald-400 shrink-0" /> {f}</li>
              ))}
            </ul>
            <button
              data-testid={`plan-cta-${p.key}`}
              disabled={p.current || busy === p.key}
              onClick={() => upgrade(p)}
              className={`mt-6 w-full px-4 py-2.5 rounded-lg font-semibold transition-colors ${
                p.current ? "bg-slate-800 text-slate-500 cursor-default" : p.highlight ? "bg-emerald-500 text-slate-950 hover:bg-emerald-400" : "bg-slate-800 text-slate-100 hover:bg-slate-700"
              } disabled:opacity-60`}
            >
              {busy === p.key ? "Redirecting…" : p.current ? p.cta : `${p.cta} · Stripe`}
            </button>
            {!p.current && p.lookup && (
              <button
                data-testid={`plan-cta-square-${p.key}`}
                disabled={busySq === p.key}
                onClick={() => squareUpgrade(p)}
                className="mt-2 w-full px-4 py-2.5 rounded-lg font-semibold border border-slate-700 text-slate-200 hover:bg-slate-800 transition-colors disabled:opacity-60"
              >
                {busySq === p.key ? "Redirecting…" : "Pay with Square"}
              </button>
            )}
          </Card>
        ))}
      </div>
    </>
  );
}
